/* global Sys */
Sys.ns("Sys.utils");

/**
 * A collection of XML utility functions.
 *
 * @class Sys.utils.XMLHelper
 * @static
 */
Sys.utils.XMLHelper = {

    /**
     * Gets the text value of the first node occurrence with the specified nodeName. Searches the rootNode recursively.
     *
     * @static
     * @param {string} nodeName The name of the node.
     * @param {Document} rootNode The node to start the search from.
     * @returns {?string} The with value or null.
     */
    getNodeValue: function(nodeName, rootNode) {
        var result;

        if (rootNode.getElementsByTagName) {
            result = rootNode.getElementsByTagName(nodeName);

            if (result.length > 0) {
                return result[0].textContent;
            }
        }

        return null;
    },

    /**
     * Searches the specified rootNode for the first occurrence of a node with the specified name and returns it.
     *
     * @static
     * @param {string} nodeName The name of the node.
     * @param {Document} rootNode The node to start the search from.
     * @returns {?Element} The first node with the specified name or null.
     */
    findNode: function(nodeName, rootNode) {
        var result;

        if (rootNode.getElementsByTagName) {
            result = rootNode.getElementsByTagName(nodeName);

            if (result.length > 0) {
                return result[0];
            }
        }

        return null;
    },

    /**
     * Searches the specified rootNode for all occurrence of nodes with the specified name and returns an array.
     *
     * @static
     * @param {string} nodeName The name of the node.
     * @param {Document} rootNode The node to start the search from.
     * @returns {Element[]} An array of all found node occurrences.
     */
    findAll: function(nodeName, rootNode) {
        if (!Sys.isEmpty(rootNode) && rootNode.getElementsByTagName) {
            return rootNode.getElementsByTagName(nodeName);
        }

        return [];
    },

    /**
     * Returns the value of the specified attribute.
     *
     * @static
     * @param {string} attributeName The name of the desired attribute.
     * @param {Element} rootNode The node whose attribute we want to get.
     * @returns {?string} The attribute value or null.
     */
    getAttributeValue: function(attributeName, rootNode) {
        if (rootNode.attributes && rootNode.hasAttribute(attributeName)) {
            return rootNode.attributes.getNamedItem(attributeName).value;
        }

        return null;
    },

    /**
     * Parses an XML element tree recursively into JSON.
     *
     * @example
     * An XML element is represented in JSON as:
     *
     *     <country name="sweden">
     *     <city>Stockholm</city>
     *     <city>Gothenburg</city>
     *     </country>
     *
     *     {
     *        tag : "country",
     *        attributes : {
     *            "name" : "sweden"
     *        },
     *        children : [
     *            {
     *                tag : "city",
     *                text : "stockholm"
     *            },
     *            {
     *                tag : "city",
     *                text : "gothenburg"
     *            }
     *         ]
     *        }
     *
     * @static
     * @param {Element} root The root element.
     * @returns {Object} JSON object.
     */
    toJSON: function(root) {
        var json = {
                tag: root.nodeName
            },
            child,
            attr,
            i,
            len;

        if (root.hasChildNodes()) {
            json.children = [];

            for (i = 0, len = root.childNodes.length; i < len; i++) {
                child = root.childNodes.item(i);

                // if the childnode is an XML element (type 1)
                if (child.nodeType === 1) {
                    json.children.push(this.toJSON(child));
                }
                // if childnode is a textnode
                else if (child.nodeType === 3) {
                    json.text = child.nodeValue.replace(/^\s+|\s+$/g, "");
                }
            }
        }

        if (root.attributes) {
            json.attributes = {};

            for (i = 0, len = root.attributes.length; i < len; i++) {
                attr = root.attributes[i];

                json.attributes[attr.nodeName] = attr.nodeValue;
            }
        }

        json.find = this.findNodeInJSON;
        json.findAll = this.findAllNodesInJSON;

        return json;
    },

    /**
     * Helper method for XML that has been converted to XML.
     *
     * @private
     * @param {string} nodeName Name of the node to search for.
     * @returns {*|null} Node or null.
     */
    findNodeInJSON: function(nodeName) {
        return Sys.find(this.children, function(child) {
            return child.tag === nodeName;
        }) || null;
    },

    /**
     * Helper method for XML that has been converted to XML.
     *
     * @private
     * @param {string} nodeName Name of the node to search for.
     * @returns {Array} Array containing found nodes.
     */
    findAllNodesInJSON: function(nodeName) {
        return this.children.filter(function(child) {
            return child.tag === nodeName;
        });
    },

    /**
     * Setup the money format from the first matching currency in the xml structure.
     *
     * @static
     * @param {Document} xml The xml data.
     * @param {string} currency The currency to setup the format for.
     * @returns {?Object} The object containing the formatting information.
     */
    getMoneyFormatFromXML: function(xml, currency) {
        var nodes = this.findAll("moneyformat", xml),
            currentCurrency,
            len,
            i;

        for (i = 0, len = nodes.length; i < len; i++) {
            currentCurrency = this.getNodeValue("iso", nodes[i]);

            if (currentCurrency === currency) {
                return this.getMoneyFormatFromNode(nodes[i]);
            }
        }

        return null;
    },

    /**
     * Setup the money formatting information from the provided xml structure.
     *
     * @example
     *     <moneyformat>
     *         <dividers thousands="," decimal="."/>
     *         <strings>
     *             <curchar after="false">€</curchar>
     *             <centchar usecent="false" after="false">c</centchar>
     *         </strings>
     *         <iso>EUR</iso>
     *         <decimals>2</decimals>
     *     </moneyformat>
     *
     * @static
     * @param {Document} xmlNode The xml node containing the money format information.
     * @returns {Object} The object containing the formatting information.
     */
    getMoneyFormatFromNode: function(xmlNode) {
        var dividersNode = this.findNode("dividers", xmlNode),
            thousandsDivider = this.getAttributeValue("thousands", dividersNode),
            decimalDivider = this.getAttributeValue("decimal", dividersNode),
            currCharNode = this.findNode("curchar", xmlNode),
            currencyChar = currCharNode.textContent,
            isCurrCharAfter = this.getAttributeValue("after", currCharNode) === "true",
            iso = this.findNode("iso", xmlNode).textContent;

        return {
            thousandsDivider: thousandsDivider,
            decimalDivider: decimalDivider,
            currencyChar: currencyChar,
            isCurrCharAfter: isCurrCharAfter,
            iso: iso
        };
    }
};
